import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';

import { MatOption } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { FormInputComponent, ObjectType, FieldValueInterface } from '../../../../tokens-and-interfaces';
import { InlineEditService } from '../../inline-edit.service';
import { UpdaterService } from '../updater.service';
import { StandardIds } from '../../../../shared-services';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  templateUrl: './marketing-consent-selector.component.html',
  styleUrls: ['../renderers.scss'],
  imports: [CommonModule, MatSelectModule, GalaxyFormFieldModule, TranslateModule, MatOption, ReactiveFormsModule],
})
export class MarketingConsentSelectorComponent extends FormInputComponent {
  private readonly inlineEditService = inject(InlineEditService);
  private readonly updaterService = inject(UpdaterService);

  override objectType: ObjectType = 'Contact';
  _fieldId = '';
  set fieldId(fieldId: string) {
    this._fieldId = fieldId;
  }
  get fieldId(): string {
    return this._fieldId;
  }
  _control: FormControl<string> = new FormControl<string>('', {
    nonNullable: true,
  });
  set control(control: FormControl<string>) {
    this._control = control;
    if (this._control.value === null) {
      this.control.setValue('');
    }
  }
  get control(): UntypedFormControl {
    return this._control;
  }

  _label = '';
  set label(label: string) {
    this._label = label;
  }
  get label(): string {
    return this._label;
  }

  protected readonly availableOptions = ['Subscribed', 'Unsubscribed'];
  private isInline = toSignal(this.inlineEditService.isInlineEdit$);

  constructor() {
    super();
    const currentConsentValue = this.control.value;
    const validSelectionFound = this.availableOptions.find((v) => v === currentConsentValue);
    if (currentConsentValue && !validSelectionFound) {
      this.control.setValue('');
      this.control.markAsTouched();
    }
  }

  set formGroup(group: UntypedFormGroup) {
    // do nothing
  }

  get hasModification(): boolean {
    return this.control.dirty;
  }

  get value(): FieldValueInterface {
    return {
      fieldId: this.fieldId,
      stringValue: this.control.value,
    };
  }

  calculateConsentMutations(): FieldValueInterface[] {
    if (this.fieldId === StandardIds.ContactMarketingEmailConsentStatus) {
      return [
        {
          fieldId: StandardIds.ContactMarketingEmailConsentSource,
          stringValue: 'CRM UI',
        },
        {
          fieldId: StandardIds.ContactMarketingEmailConsentTimestamp,
          dateValue: new Date(),
        },
      ];
    }
    if (this.fieldId === StandardIds.ContactSMSConsentStatus) {
      return [
        {
          fieldId: StandardIds.ContactSMSConsentSource,
          stringValue: 'CRM UI',
        },
        {
          fieldId: StandardIds.ContactSMSConsentTimestamp,
          dateValue: new Date(),
        },
      ];
    }
    return [];
  }

  applyConsentMutations(fieldValues: FieldValueInterface[]): void {
    for (const mutation of fieldValues) {
      this.updaterService.updateFieldValue(mutation);
    }
  }

  saveField(): void {
    const consentMutations = this.calculateConsentMutations();
    if (!this.isInline()) {
      this.applyConsentMutations(consentMutations);
      return;
    }
    this.inlineEditService.saveInlineEdit(this.objectType, [
      {
        fieldId: this.fieldId,
        stringValue: this.control.value,
      },
      ...consentMutations,
    ]);
  }
}
